import { ReactNode, useEffect, useId, useRef } from 'react';

/**
 * Ventana de detalle (0.44.0) — la de "abrir una barra y ver qué hay adentro": Qué piden → los textos de una familia,
 * Mapa → las direcciones que no se encontraron. Con el estilo de la app (tarjeta sobre fondo oscurecido), nada del
 * navegador: `role="dialog"`, `aria-modal`, el título ligado, el foco adentro y de regreso al cerrar; Escape, el fondo
 * o "Cerrar" la cierran. Va encima de las gráficas ampliadas (1000) y debajo de los diálogos de la app (5000): un
 * aviso puede salir sobre ella, y su Escape no la cierra a ella.
 */
export function Ventana({ titulo, subtitulo, onClose, children, ancho = 760, datos }: {
  titulo: ReactNode; subtitulo?: ReactNode; onClose: () => void; children: ReactNode; ancho?: number; datos?: string;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    const antes = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.focus();
    // Con una ventana encima de otra (el perfil → lo que dijo cada quien), Escape cierra sólo la de ARRIBA (la última abierta).
    const tecla = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || document.querySelector('[data-dialogo]')) return;
      const todas = document.querySelectorAll('[data-ventana]');
      if (todas[todas.length - 1] !== panel.current) return;
      e.stopPropagation(); onClose();
    };
    document.addEventListener('keydown', tecla);
    return () => { document.removeEventListener('keydown', tecla); document.body.style.overflow = overflow; antes?.focus?.(); };
  }, []);
  return (
    <div onClick={onClose} data-ventana-fondo style={{ position: 'fixed', inset: 0, background: 'rgba(40,33,40,0.45)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div ref={panel} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={id} data-ventana={datos || ''}
        onClick={(e) => e.stopPropagation()} className="card"
        style={{ width: `min(${ancho}px, 96vw)`, maxHeight: '88vh', padding: 18, overflow: 'auto', outline: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
          <h3 id={id} style={{ margin: 0, fontSize: 17, color: 'var(--gold-dark)' }}>{titulo}</h3>
          <button type="button" className="btn-ghost" onClick={onClose} data-ventana-cerrar>Cerrar</button>
        </div>
        {subtitulo && <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5, marginBottom: 10 }}>{subtitulo}</div>}
        {children}
      </div>
    </div>
  );
}
